import { ISettings } from '@admin/pages/settings-manager/interfaces/settings.interface';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  inject,
  model,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AnalyticsService } from '@core/services/analytics.service';
import { AuthService } from '@core/services/auth.service';
import { NotificationPermissionService } from '@core/services/notification-permission.service';
import { BidService } from '@shared/services/bid.service';
import { LanguageService } from '@shared/services/language.service';
import { MessageService } from '@shared/services/message.service';
import { MetaManagerService } from '@shared/services/meta-manager.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { SearchService } from '@shared/services/search.service';
import { SEOService } from '@shared/services/seo.service';
import { SSR_ACCEPT_LANGUAGE, SSR_PATH } from '@shared/tokens/server.token';
import { environment } from 'src/environments/environment';
import { LoginService } from './login/services/login.service';
import { TagService } from './tags-manager/services/tag.service';
import { UserService } from './users-manager/services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  appSettings = model<ISettings>();
  isLoaded = signal(false);
  showHeader = signal(true);
  showFooter = signal(true);
  showAllFooter = signal(true);
  withLoader = signal(true);
  hideSearch = signal(false);

  metaService = inject(MetaManagerService);

  // Injected services
  public readonly loginService = inject(LoginService);
  public readonly authService = inject(AuthService);
  public readonly userService = inject(UserService);
  private readonly languageService = inject(LanguageService);
  private readonly searchService = inject(SearchService);
  private readonly publicGenericService = inject(PublicGenericService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly ssrPath = inject(SSR_PATH, { optional: true });
  private readonly ssrLang = inject(SSR_ACCEPT_LANGUAGE, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly _renderer = inject(Renderer2);
  private readonly seoService = inject(SEOService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly platformService = inject(PlatformService);
  private readonly tagService = inject(TagService);
  private readonly updates = inject(SwUpdate);
  private readonly bidService = inject(BidService);
  private readonly notificationPermissionService = inject(
    NotificationPermissionService
  );

  updateApp() {
    this.updates.activateUpdate().then(() => document.location.reload());
  }


  constructor() {
    const lang =
      this.ssrLang ??
      (this.ssrPath ?? globalThis.window?.location?.pathname)?.split('/')[1];
    const langSpecified = lang === 'it' || lang === 'en';
    const defaultLanguage = lang === 'it' ? 'it' : 'en';
    this.document.documentElement.lang = defaultLanguage;

    if (!environment.production) {
      this.metaService.setNoIndex();
    }

    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        this.hideHeaderFooter(event);
        this.hideSearch.set(await this.tagService.isSpecialTagPage());
        this.withLoader.set(!event?.url.includes('/event/'));
        this.seoService.updateCanonicalUrl(
          environment.canonicalUrl + event.url.slice(1)
        );

        if (isPlatformBrowser(this._platformId)) {
          this.bidService.checkAndShowReservePromptForRoute(
            event.urlAfterRedirects ?? event.url
          );
        }
      }
    });

    this.platformService.runOnServer(() => {
      this.languageService.userSelectedLanguage = {
        idLanguage: defaultLanguage === 'it' ? 1 : 2,
        code: defaultLanguage,
        description: '',
      };
    });

    this.platformService.runOnBrowser(() => {
      const langUser = window.navigator.language?.toLowerCase() ?? 'en';
      this.withLoader.set(!location.pathname.includes('/event/'));

      if (this.updates.isEnabled) {
        this.updates.versionUpdates.subscribe((event) => {
          if (event.type === 'VERSION_READY') {
            const shouldUpdate = confirm(
              'A new version is available. Reload to update?'
            );
            if (shouldUpdate) {
              this.updateApp();
            }
          }
        });
      }

      if (
        localStorage &&
        localStorage.getItem('langId') != null &&
        localStorage.getItem('langCode') != null
      ) {
        const savedLang = localStorage.getItem('langCode');
        let toSetLang = savedLang;
        if (savedLang !== defaultLanguage && langSpecified) {
          localStorage.setItem('langCode', defaultLanguage);
          localStorage.setItem('langId', defaultLanguage === 'it' ? '1' : '2');
          toSetLang = defaultLanguage;
        }

        this.languageService.userSelectedLanguage = {
          idLanguage: parseInt(localStorage.getItem('langId')),
          code: toSetLang,
          description: '',
        };
      } else {
        if (defaultLanguage === 'it' || langUser.startsWith('it')) {
          this.languageService.userSelectedLanguage = {
            idLanguage: 1,
            code: 'it',
            description: '',
          };
        } else {
          this.languageService.userSelectedLanguage = {
            idLanguage: 2,
            code: 'en',
            description: '',
          };
        }
      }
    });
  }

  ngOnInit(): void {
    this.languageService.fillLanguages();

    this.isLoaded.set(!!this.appSettings);

    // Inject Organization schema for +10% GEO visibility
    // this.injectOrganizationSchema();

    this.publicGenericService.getGlobalSettings().subscribe((dataR) => {
      this.appSettings.set(dataR);
      this.isLoaded.set(true);
    });

    if (!this.authService.isPingAlreadyExecuted) {
      this.authService.isPingAlreadyExecuted = true;
      this.loginService.ping().subscribe((responseBody) => {
        if (responseBody == null) return;

        const username = responseBody.username;
        if (this.authService.userLogged?.username === '') {
          this.userService.getByUsername(username).subscribe((response) => {
            this.authService.authResult = 1;
            this.authService.userLogged = response;
            this.messageService.startTimer();

            // Prompt for browser notifications once for authenticated users
            this.notificationPermissionService.promptIfNeeded();

            this.analyticsService.trackInit({
              idUser: this.authService.userLogged.idUser,
              email: this.authService.userLogged.username,
              firstName:
                this.authService.userLogged.entityFullName.split(' ')[0],
              lastName: this.authService.userLogged.entityFullName
                .split(' ')
                .slice(1)
                .join(' '),
            });

            if (isPlatformBrowser(this._platformId)) {
              this.bidService.checkAndShowReservePromptForRoute(
                this.router.url
              );
            }
          });
        } else if (this.authService.isAuthenticated()) {
          this.messageService.startTimer();
          // Prompt for browser notifications once for authenticated users
          this.notificationPermissionService.promptIfNeeded();

          if (isPlatformBrowser(this._platformId)) {
            this.bidService.checkAndShowReservePromptForRoute(this.router.url);
          }
        }
      });
    }

    if (isPlatformBrowser(this._platformId)) {
      this.searchService.filters.idLanguage =
        this.languageService.userSelectedLanguage.idLanguage;
      this.searchService.getAllTypes().subscribe((dataR) => {
        this.searchService.auctionTypeList = dataR;
      });
      this.publicGenericService.getAllDetails();
      this.publicGenericService.getAllCountries();
    }
    this.publicGenericService.getPriceRange();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      const loader = this._renderer.selectRootElement('#loader');
      if (loader.style.display != 'none') loader.style.display = 'none';

      // Set dark background for eventnow pages
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (
            event.url.includes('/eventnow/') ||
            event.url.includes('/event/')
          ) {
            this._renderer.addClass(this.document.body, 'eventnow-dark');
            this._renderer.addClass(
              this.document.documentElement,
              'eventnow-dark'
            );
          } else {
            this._renderer.removeClass(this.document.body, 'eventnow-dark');
            this._renderer.removeClass(
              this.document.documentElement,
              'eventnow-dark'
            );
          }
        }
      });
    }
  }

  hideHeaderFooter(location) {
    if (location.url.includes('/event/')) {
      this.showHeader.set(false);
      this.showFooter.set(false);
      this.showAllFooter.set(false);
    } else if (location.url.includes('/reset-password')) {
      // Hide header and full footer on reset password page
      this.showHeader.set(false);
      this.showFooter.set(false);
      this.showAllFooter.set(false);
    } else if (location.url.includes('/eventnow/')) {
      this.showHeader.set(false);
      this.showFooter.set(true);
      this.showAllFooter.set(false);
    } else {
      this.showHeader.set(true);
      this.showFooter.set(true);
      this.showAllFooter.set(true);
    }
  }
}
